import React, {Component} from 'react';
import {connect} from 'react-redux';
import {NavLink as Link, withRouter} from 'react-router-dom';
import styled, {keyframes} from 'styled-components';
import {Row, Col, Typography, Divider} from 'antd';
import {
  InstagramOutlined,
  TwitterOutlined,
  LinkedinFilled,
  YoutubeFilled,
  MailOutlined,
  HeartFilled,
  ArrowUpOutlined,
} from '@ant-design/icons';

import logo from '../../../logo.png';

const {Title, Paragraph, Text} = Typography;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;

// Styled Components
const FooterContainer = styled.footer`
  background: ${props => props.theme.colors?.neutral?.[950] || '#0a0a0a'};
  border-top: 1px solid ${props => props.theme.colors?.neutral?.[800] || '#262626'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 0%, rgba(249, 115, 22, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing?.[16] || '4rem'}
    ${props => props.theme.spacing?.[6] || '1.5rem'} ${props => props.theme.spacing?.[8] || '2rem'};
  position: relative;
  z-index: 2;

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[12] || '3rem'}
      ${props => props.theme.spacing?.[4] || '1rem'}
      ${props => props.theme.spacing?.[6] || '1.5rem'};
  }
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  animation: ${fadeInUp} 1s ease-out;

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    align-items: center;
    text-align: center;
    margin-bottom: ${props => props.theme.spacing?.[8] || '2rem'};
  }
`;

const Logo = styled.img`
  height: 60px;
  width: auto;
  margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
  transition: transform ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};

  &:hover {
    transform: scale(1.05);
  }
`;

const BrandDescription = styled(Paragraph)`
  && {
    color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
    font-size: ${props => props.theme.typography?.fontSize?.base || '1rem'};
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
    max-width: 300px;
    margin-bottom: ${props => props.theme.spacing?.[6] || '1.5rem'};

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      max-width: none;
    }
  }
`;

const FooterSection = styled.div`
  animation: ${fadeInUp} 1s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    text-align: center;
    margin-bottom: ${props => props.theme.spacing?.[8] || '2rem'};
  }
`;

const SectionTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.bold || '700'};
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const FooterLink = styled(Link)`
  display: block;
  color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
  text-decoration: none;
  padding: ${props => props.theme.spacing?.[2] || '0.5rem'} 0;
  transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  position: relative;

  &:hover {
    color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    transform: translateX(5px);
  }

  &::before {
    content: '';
    position: absolute;
    left: -10px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 2px;
    background: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    transition: width ${props => props.theme.animation?.duration?.normal || '300ms'}
      ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  }

  &:hover::before {
    width: 6px;
  }
`;

const ExternalLink = styled.a`
  display: block;
  color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
  text-decoration: none;
  padding: ${props => props.theme.spacing?.[2] || '0.5rem'} 0;
  transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  position: relative;

  &:hover {
    color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    transform: translateX(5px);
  }

  &::before {
    content: '';
    position: absolute;
    left: -10px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 2px;
    background: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    transition: width ${props => props.theme.animation?.duration?.normal || '300ms'}
      ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  }

  &:hover::before {
    width: 6px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing?.[4] || '1rem'};
  margin-top: ${props => props.theme.spacing?.[4] || '1rem'};

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    justify-content: center;
  }
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.colors?.glass?.light || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
  transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  animation: ${float} 3s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};

  &:hover {
    background: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    color: white;
    transform: translateY(-3px);
    box-shadow: ${props =>
      props.theme.boxShadow?.lg ||
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'};
  }

  .anticon {
    font-size: 1.25rem;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${props => props.theme.colors?.neutral?.[800] || '#262626'};
  padding: ${props => props.theme.spacing?.[6] || '1.5rem'} 0;
  margin-top: ${props => props.theme.spacing?.[12] || '3rem'};
  animation: ${fadeInUp} 1s ease-out 0.8s both;
`;

const CopyrightText = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[500] || '#737373'};
    font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      text-align: center;
      display: block;
      margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
    }
  }
`;

const MadeWithLove = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing?.[2] || '0.5rem'};
  color: ${props => props.theme.colors?.neutral?.[500] || '#737373'};
  font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};

  .heart-icon {
    color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    animation: ${pulse} 2s ease-in-out infinite;
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    justify-content: center;
  }
`;

const BackToTop = styled.button`
  position: fixed;
  bottom: ${props => props.theme.spacing?.[6] || '1.5rem'};
  right: ${props => props.theme.spacing?.[6] || '1.5rem'};
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props =>
    props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
  border: none;
  color: white;
  cursor: pointer;
  box-shadow: ${props =>
    props.theme.boxShadow?.lg ||
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'};
  transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
  z-index: 1000;
  opacity: ${props => (props.visible ? 1 : 0)};
  transform: translateY(${props => (props.visible ? 0 : '100px')});

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props =>
      props.theme.boxShadow?.xl ||
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
  }

  .anticon {
    font-size: 1.25rem;
  }
`;

class Footer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showBackToTop: false,
    };
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    this.setState({
      showBackToTop: scrollTop > 300,
    });
  };

  scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  render() {
    const currentYear = new Date().getFullYear();

    return (
      <>
        <FooterContainer>
          <FooterContent>
            <Row gutter={[48, 32]}>
              {/* Brand Section */}
              <Col xs={24} md={8}>
                <LogoSection>
                  <Link to="/">
                    <Logo src={logo} alt="CLLCTVE Logo" />
                  </Link>
                  <BrandDescription>
                    Empowering the next generation of creatives by connecting college students with
                    brands targeting Gen-Z consumers.
                  </BrandDescription>
                  <SocialLinks>
                    <SocialIcon
                      href="https://www.instagram.com/cllctve/?hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      delay="0s"
                    >
                      <InstagramOutlined />
                    </SocialIcon>
                    <SocialIcon
                      href="https://twitter.com/cllctve"
                      target="_blank"
                      rel="noopener noreferrer"
                      delay="0.1s"
                    >
                      <TwitterOutlined />
                    </SocialIcon>
                    <SocialIcon
                      href="https://www.linkedin.com/company/cllctve/"
                      target="_blank"
                      rel="noopener noreferrer"
                      delay="0.2s"
                    >
                      <LinkedinFilled />
                    </SocialIcon>
                    <SocialIcon
                      href="https://www.youtube.com/channel/UCM-3kG3xwxjGGdaZNxxT0bg"
                      target="_blank"
                      rel="noopener noreferrer"
                      delay="0.3s"
                    >
                      <YoutubeFilled />
                    </SocialIcon>
                  </SocialLinks>
                </LogoSection>
              </Col>

              {/* Platform Links */}
              <Col xs={24} sm={12} md={4}>
                <FooterSection delay="0.2s">
                  <SectionTitle level={4}>Platform</SectionTitle>
                  <FooterLink to="/">Home</FooterLink>
                  <FooterLink to="/creative/signup">Join as Creative</FooterLink>
                  <FooterLink to="/brand/signup">Join as Brand</FooterLink>
                  <ExternalLink
                    href="https://medium.com/cllctve"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Blog
                  </ExternalLink>
                </FooterSection>
              </Col>

              {/* Resources */}
              <Col xs={24} sm={12} md={4}>
                <FooterSection delay="0.4s">
                  <SectionTitle level={4}>Resources</SectionTitle>
                  <ExternalLink
                    href="https://www.techstars.com/accelerators/los-angeles"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    About Techstars
                  </ExternalLink>
                  <ExternalLink href="mailto:hello@cllctve.com">Contact Us</ExternalLink>
                  <ExternalLink href="mailto:support@cllctve.com">Support</ExternalLink>
                </FooterSection>
              </Col>

              {/* Legal */}
              <Col xs={24} sm={12} md={4}>
                <FooterSection delay="0.6s">
                  <SectionTitle level={4}>Legal</SectionTitle>
                  <FooterLink to="/privacy">Privacy Policy</FooterLink>
                  <FooterLink to="/terms">Terms of Service</FooterLink>
                  <FooterLink to="/cookies">Cookie Policy</FooterLink>
                </FooterSection>
              </Col>

              {/* Contact */}
              <Col xs={24} sm={12} md={4}>
                <FooterSection delay="0.8s">
                  <SectionTitle level={4}>Get in Touch</SectionTitle>
                  <ExternalLink href="mailto:hello@cllctve.com">
                    <MailOutlined style={{marginRight: '8px'}} />
                    hello@cllctve.com
                  </ExternalLink>
                  <div style={{marginTop: '1rem', color: '#a3a3a3', fontSize: '0.875rem'}}>
                    Los Angeles, CA
                  </div>
                </FooterSection>
              </Col>
            </Row>

            <FooterBottom>
              <Row justify="space-between" align="middle">
                <Col xs={24} md={12}>
                  <CopyrightText>© {currentYear} CLLCTVE. All rights reserved.</CopyrightText>
                </Col>
                <Col xs={24} md={12}>
                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <MadeWithLove>
                      Made with <HeartFilled className="heart-icon" /> for creatives
                    </MadeWithLove>
                  </div>
                </Col>
              </Row>
            </FooterBottom>
          </FooterContent>
        </FooterContainer>

        <BackToTop
          visible={this.state.showBackToTop}
          onClick={this.scrollToTop}
          aria-label="Back to top"
        >
          <ArrowUpOutlined />
        </BackToTop>
      </>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    authenticated: state.auth.authenticated,
  };
};

export default withRouter(connect(mapStateToProps)(Footer));
